import type {
  AssuntoDoChat,
  IAdministracaoIA,
  IDadosContratoVigente,
  IPessoaDaAdministracao,
  IRascunhoDaAdministracao,
  TipoVinculoSocio,
} from '../../../../hooks/useSocietario';
import type { ISocioFormValues } from './schema';

// Chat da administração (29/09): quem aparece para a IA, com o que o formulário diz. A IA não
// inventa pessoa nem qualificação; o vínculo muda no passo Sócios.

const ADMINISTRA: (TipoVinculoSocio | null | undefined)[] = [
  'socio_administrador',
  'administrador',
];

const soDigitos = (documento?: string | null): string => (documento ?? '').replace(/\D/g, '');
const nomeNormalizado = (nome?: string | null): string =>
  (nome ?? '').trim().replace(/\s+/g, ' ').toUpperCase();

/**
 * Endereço do formulário em texto. Quem entra (e quem marcou o aspecto Endereço) preenche o
 * estruturado; o campo de texto é o do vigente (review de 29/09: administrador novo saía sem domicílio).
 */
const enderecoEmTexto = (s: ISocioFormValues): string => {
  const e = s.endereco_estruturado;
  if (e?.logradouro) {
    const t = (parte: unknown) => String(parte ?? '').trim();
    // Como o back escreve (`ponte._endereco_corrido`): o CEP sem a palavra saiu assim no contrato (#159).
    const cidade = [t(e.cidade), t(e.uf)].filter(Boolean).join(' - ');
    return [
      t(e.logradouro),
      t(e.numero),
      t(e.complemento),
      t(e.bairro),
      cidade,
      t(e.cep) && `CEP ${t(e.cep)}`,
    ]
      .filter(Boolean)
      .join(', ');
  }
  return (s.endereco ?? '').trim();
};

/** Qualificação a partir do formulário, só com o que foi preenchido. */
export function qualificacaoDoFormulario(s: ISocioFormValues): string {
  const endereco = enderecoEmTexto(s);
  if (s.tipo_pessoa === 'pj') {
    return [
      s.cnpj ? `CNPJ nº ${s.cnpj}` : '',
      s.nire ? `NIRE ${s.nire}` : '',
      endereco ? `sede em ${endereco}` : '',
      s.representante_nome ? `representada por ${s.representante_nome}` : '',
    ]
      .filter(Boolean)
      .join(', ');
  }
  const rg = s.rg ? `RG nº ${s.rg}${s.rg_orgao_emissor ? ` ${s.rg_orgao_emissor}` : ''}` : '';
  return [
    s.nacionalidade ?? '',
    s.estado_civil ?? '',
    s.profissao ?? '',
    rg,
    s.cpf ? `CPF nº ${s.cpf}` : '',
    endereco ? `residente e domiciliado(a) em ${endereco}` : '',
  ]
    .map(parte => String(parte).trim())
    .filter(Boolean)
    .join(', ');
}

export function pessoasDaAdministracao(
  socios: ISocioFormValues[] | undefined,
  vigente: IDadosContratoVigente | null | undefined,
): IPessoaDaAdministracao[] {
  // Casa pelo documento e, sem ele, pelo nome: sócio lido sem CPF que ganhou CPF no formulário não
  // pode aparecer como "Passa a administrar".
  const porDocumento = new Map<string, boolean>();
  const porNome = new Map<string, boolean>();
  (vigente?.quadro_socios_atual ?? []).forEach(v => {
    const administra = ADMINISTRA.includes(v?.tipo_vinculo);
    const doc = soDigitos(v?.cpf || v?.cnpj);
    if (doc) porDocumento.set(doc, administra);
    if (v?.nome) porNome.set(nomeNormalizado(v.nome), administra);
  });
  const administraHoje = (s: ISocioFormValues): boolean =>
    porDocumento.get(soDigitos(s.cpf || s.cnpj)) ?? porNome.get(nomeNormalizado(s.nome)) ?? false;
  return (socios ?? [])
    .filter(s => s?.nome?.trim())
    .map(s => {
      const entra = s.acao === 'adicionar';
      const sai = s.acao === 'sair';
      const permanece = sai && !!s.permanece_como_administrador;
      const hoje = !entra && administraHoje(s);
      const depois = sai ? permanece : ADMINISTRA.includes(s.tipo_vinculo);
      let situacao: IPessoaDaAdministracao['situacao'] = 'continua';
      if (entra) situacao = 'entra';
      else if (sai) situacao = 'sai';
      return {
        nome: s.nome.trim(),
        situacao,
        socio: !(sai || s.tipo_vinculo === 'administrador'),
        administra_hoje: hoje,
        administra_depois: depois,
        // Quem passa a administrar precisa da qualificação no texto; os outros, não.
        qualificacao: depois && !hoje ? qualificacaoDoFormulario(s) : '',
      };
    });
}

// Pelos valores, nunca pela ordem das chaves: o jsonb do banco reordena ("texto" antes de "titulo") e o
// aprovado recém-gravado vem na ordem do pydantic (E2E de 29/09: aprovado aparecia como alterado).
const textoDoRascunho = (r: IRascunhoDaAdministracao) =>
  JSON.stringify([
    ...[r.parte_a, r.parte_b].map(parte =>
      parte.map(c => [c.titulo, c.texto, c.substitui, c.depois_de ?? 0]),
    ),
    [...(r.excluidas ?? [])].sort(),
    r.o_que_mudou,
    [...(r.textos_prontos ?? [])].sort(),
  ]);

export const mesmoTexto = (
  a?: IRascunhoDaAdministracao | null,
  b?: IRascunhoDaAdministracao | null,
): boolean => !!a && !!b && textoDoRascunho(a) === textoDoRascunho(b);

// A qualificação conta: a de quem passa a administrar vai ao texto como está (2ª review de 29/09:
// CPF ou endereço corrigido no Sócios depois de aprovar saía com o dado velho).
const vinculosDe = (pessoas: IPessoaDaAdministracao[]) =>
  JSON.stringify(
    pessoas
      .map(p => [
        nomeNormalizado(p.nome),
        p.situacao,
        p.socio,
        p.administra_depois,
        // Sem separadores nem a palavra CEP: o endereço passou de "Cidade, UF, 00000-000" para
        // "Cidade - UF, CEP 00000-000" (15/10) e a redação aprovada antes disso não pode travar.
        (p.qualificacao ?? '')
          .replace(/\bCEP\b/gi, ' ')
          .replace(/[\s,;:/–-]+/g, ' ')
          .trim(),
      ])
      .sort((a, b) => String(a[0]).localeCompare(String(b[0]))),
  );

/**
 * Quem administra no passo Sócios mudou depois da aprovação (review de 29/09: a redação aprovada
 * seguia valendo e o contrato saía contradizendo o quadro). Aprovado sem as pessoas (antes disso): não
 * compara.
 */
export function quemAdministraMudou(
  estado: IAdministracaoIA | null | undefined,
  pessoasAgora: IPessoaDaAdministracao[] | undefined,
): boolean {
  const naAprovacao = estado?.aprovado?.pessoas;
  if (!naAprovacao || !pessoasAgora) return false;
  return vinculosDe(naAprovacao) !== vinculosDe(pessoasAgora);
}

/**
 * A redação na tela foi feita com outro quadro de quem administra (2ª review de 29/09: pedida com
 * Fulano, trocado para Beltrano no Sócios, e aprovar gravava as pessoas novas com o texto do Fulano).
 * Rascunho sem as pessoas (antes disso): não compara.
 */
export function redacaoForaDoQuadro(
  estado: IAdministracaoIA | null | undefined,
  pessoasAgora: IPessoaDaAdministracao[] | undefined,
): boolean {
  const naRedacao = estado?.pessoas_do_rascunho;
  if (!naRedacao || !pessoasAgora || !estado?.rascunho) return false;
  return vinculosDe(naRedacao) !== vinculosDe(pessoasAgora);
}

/**
 * Com o tipo Administração marcado, o chat é obrigatório (decisão do user, 29/09): o que ainda falta
 * para seguir, ou null. `editadaNaTela`: o operador mexeu no texto e não aprovou de novo.
 */
export function pendenciaDaAdministracao(
  estado: IAdministracaoIA | null | undefined,
  editadaNaTela?: boolean,
  pessoasAgora?: IPessoaDaAdministracao[],
  assunto: AssuntoDoChat = 'administracao',
): string | null {
  if (estado?.pendente?.mensagem) {
    return 'A IA ainda está respondendo o último pedido: espere a resposta para seguir.';
  }
  if (!estado?.aprovado) {
    return assunto === 'clausulas'
      ? 'Peça a redação na conversa ou marque um texto pronto, e clique em "Usar esta redação".'
      : 'Peça a redação na conversa (ou use "Redigir pelo que marquei em Sócios") e clique em "Usar esta redação".';
  }
  if (editadaNaTela || (estado.rascunho && !mesmoTexto(estado.rascunho, estado.aprovado))) {
    // Outras cláusulas: desmarcado o último texto pronto, a redação fica vazia e o botão some.
    return assunto === 'clausulas'
      ? 'A redação mudou depois de aprovada: clique em "Usar esta redação" de novo (se não sobrou nada, use "Recomeçar conversa").'
      : 'A redação mudou depois de aprovada: clique em "Usar esta redação" de novo.';
  }
  if (quemAdministraMudou(estado, pessoasAgora)) {
    return 'Quem administra (ou a qualificação de quem passa a administrar) mudou no passo Sócios depois da aprovação: peça a redação de novo na conversa e aprove.';
  }
  return null;
}

/** A frase do vínculo, para a tela. */
export function vinculoNaTela(p: IPessoaDaAdministracao): string {
  if (p.administra_hoje && p.administra_depois) return 'Continua administrando';
  if (p.administra_depois) return 'Passa a administrar';
  if (p.administra_hoje) return 'Deixa a administração';
  return 'Não administra';
}

/**
 * Um trecho da cláusula na leitura: parágrafo (com o rótulo "Parágrafo Primeiro:" em destaque) ou
 * item de lista (rótulo "•" quando a marca era "- ", "• "; "a)", "(i)", "1." ficam no texto).
 */
export interface ITrechoDaClausula {
  tipo: 'paragrafo' | 'item';
  rotulo: string;
  texto: string;
}

const ROTULO_DE_PARAGRAFO =
  /^((?:Parágrafo|Paragrafo)\s+(?:Único|Unico|Primeiro|Segundo|Terceiro|Quarto|Quinto|Sexto|Sétimo|Oitavo|Nono|Décimo)|§\s*\d+\s*[º°o]?)\s*[.:–-]?\s*/i;
const MARCA_DE_ITEM = /^(?:[-•*]\s+|\(?[a-z]\)\s+|\(?[ivx]+\)\s+|\d+[.)]\s+)/i;

/** O texto da cláusula em trechos, linha a linha, para ler na tela como no documento. */
export function trechosDaClausula(texto: string): ITrechoDaClausula[] {
  return (texto ?? '')
    .split('\n')
    .map(linha => linha.trim())
    .filter(Boolean)
    .map(linha => {
      const rotulo = ROTULO_DE_PARAGRAFO.exec(linha);
      if (rotulo) {
        return {
          tipo: 'paragrafo',
          rotulo: rotulo[0].trim(),
          texto: linha.slice(rotulo[0].length),
        } as ITrechoDaClausula;
      }
      const item = MARCA_DE_ITEM.exec(linha);
      if (item) {
        const bolinha = /^[-•*]\s/.test(item[0]);
        return {
          tipo: 'item',
          rotulo: bolinha ? '•' : '',
          texto: bolinha ? linha.slice(item[0].length) : linha,
        } as ITrechoDaClausula;
      }
      return { tipo: 'paragrafo', rotulo: '', texto: linha } as ITrechoDaClausula;
    });
}
